import React, { useMemo } from "react";
import { useTheme } from "@mui/material";
import { CpuChipIcon } from "@heroicons/react/24/outline";
import { can, readSession } from "../../utils/organisationApi";
import { formatNumber } from "../../utils/subscriptionData";

const plural = (n, word) => `${formatNumber(n)} ${word}${n === 1 ? "" : "s"}`;

// What the person has and what they will have left, in two plain lines. Unlimited plans have nothing to count.
const Balance = ({ summary, count }) => {
  const theme = useTheme();
  if (summary.unlimited) return <p className="shrink-0 text-xs font-medium">Unlimited credits</p>;
  return (
    <p className="shrink-0 text-right text-xs leading-snug" style={{ color: theme.palette.text.secondary }}>
      You have <span className="font-semibold text-black">{plural(summary.remaining, "credit")}</span>
      <span className="block">
        {summary.short ? (
          <span className="font-semibold" style={{ color: "#b40909" }}>
            {plural(summary.cost - summary.remaining, "credit")} short
          </span>
        ) : (
          <>
            <span className="font-semibold" style={{ color: theme.palette.info.main }}>
              {plural(summary.after, "credit")} left
            </span>{" "}
            after {count > 1 ? "these interviews" : "this interview"}
          </>
        )}
      </span>
    </p>
  );
};

// kind: "use" (AI is being switched on), "refund" (AI is being switched off), "already" (AI stays on),
// "free" (human interviewer). `summary` is creditSummary(); `minutes` and `count` describe the interview(s).
// Shown next to the duration, so the length and its cost are read together before anything is confirmed.
const AICreditCost = ({ kind, summary, minutes, count, durationText }) => {
  const theme = useTheme();
  const canBill = useMemo(() => can(readSession(), "manageBilling"), []);

  if (kind === "free") {
    return <p className="text-xs" style={{ color: theme.palette.text.secondary }}>A human interview doesn't use AI credits.</p>;
  }

  const next = canBill ? (
    <a href="/settings?tab=plan" target="_blank" rel="noopener noreferrer" className="font-semibold underline">
      Add credits or upgrade<span className="sr-only"> (opens in a new tab)</span>
    </a>
  ) : (
    "Contact your Organisation Admin."
  );

  let title;
  let detail;
  let warning = "";
  if (kind === "use") {
    title = `Uses ${plural(summary.cost, "AI credit")}`;
    detail = `${durationText} = ${plural(summary.perInterview, "credit")}${count > 1 ? ` each × ${plural(count, "candidate")}` : ""}`;
    if (summary.inactive) warning = "Your organisation doesn't have an active plan, so AI interviews can't be scheduled.";
    else if (summary.none) {
      warning =
        summary.remaining > 0
          ? `Insufficient AI credits: this needs ${plural(summary.perInterview, "credit")} and you have ${plural(summary.remaining, "credit")}.`
          : "Insufficient AI credits: you have none left.";
    }
    else if (summary.short) warning = `Only ${plural(summary.covered, "interview")} of ${count} can use AI with the credits you have.`;
  } else if (kind === "refund") {
    title = "1 AI credit comes back";
    detail = "Turning AI off before the interview starts gives its credit back.";
  } else {
    title = "No extra credit";
    detail = "This interview already has its AI credit. Rescheduling or switching AI modes doesn't use another.";
  }

  const alert = Boolean(warning);
  const tone = summary.none ? "border-red-300 bg-red-50" : alert ? "border-amber-300 bg-amber-50" : "border-[#d5e0f2] bg-[#f3f6fb]";

  return (
    <div role={alert ? "alert" : "status"} aria-live="polite" className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-3 py-2 ${tone}`}>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm" style={{ color: theme.palette.info.main }} aria-hidden="true">
        <CpuChipIcon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs" style={{ color: theme.palette.text.secondary }}>
          {detail}
        </p>
      </div>
      {kind === "use" && summary.known && <Balance summary={summary} count={count} />}
      {warning && (
        <p className="w-full text-xs font-medium text-amber-900">
          {warning} {next}
        </p>
      )}
    </div>
  );
};

export default AICreditCost;
